import { createColumnHelper, getCoreRowModel, Table, useReactTable } from '@tanstack/react-table';
import { formatDistanceToNow } from 'date-fns';
import React, { useEffect } from 'react';
import { BoardExtendedData } from '../../../../api/ApiTypes';
import { Colors, remInPx } from '../../../../ui';
import { Checkbox } from '../../../../ui/Checkbox';
import { Tip } from '../../../../ui/Tip';
import { DataTable } from '../../../common/DataTable';
import { SortMode } from '../../../common/Sorter';
import { Copy, Flexer } from '../../../dashboard/elements/LayoutElements';
import { TaskTableTheme } from '../../../dashboard/tasks/TaskTableTheme';
import { BoardInfoButton } from '../../shared/BoardInfoButton';
import { UserInfoButton } from '../../shared/UserInfoButton';
import { WorkspaceInfoButton } from '../../shared/WorkspaceInfoButton';
import { BadgeCell, TextCell } from '../common/TableElements';

const columnHelper = createColumnHelper<BoardExtendedData>();

const columns = [
    columnHelper.display({
        id: 'select',
        size: remInPx(5),
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected()
                }
                onChange={(value) => table.toggleAllPageRowsSelected(!!value)}
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
            />
        ),
        enableSorting: false,
        enableHiding: false,
    }),
    columnHelper.accessor(row => row!, {
        id: 'label',
        size: remInPx(16),
        header: () => <span>Board</span>,
        cell: info => {
            const value = info.getValue();
            if (!value) return null;
            // return <BoardBadgeButton size=".875" label={value.label} href={`${Host.baseUrl}/${value.id}`} />;
            return <Flexer height="100%" flex="row" align="center">
                <BoardInfoButton board={value} />
            </Flexer>;
        }
    }),
    columnHelper.accessor(row => row.user, {
        id: 'userId',
        size: remInPx(16),
        header: () => <span>Creator</span>,
        cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return <Flexer height="100%" flex="row" align="center">
                <UserInfoButton value={value} />
            </Flexer>;
        }
    }),
    columnHelper.accessor(row => row.workspace, {
        id: 'workspaceId',
        size: remInPx(16),
        header: () => <span>Team</span>,
        cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return <Flexer height="100%" flex="row" align="center">
                <WorkspaceInfoButton workspace={value} />
            </Flexer>;
        }
    }),
    columnHelper.accessor(row => row.folder, {
        id: 'folderId',
        size: remInPx(16),
        header: () => <span>Folder</span>,
        cell: info => {
            const value = info.getValue();
            if (!value) return null;
            return <Tip rest={50} text={value.id}>
                <TextCell value={value.label} />
            </Tip>;
        }
    }),
    columnHelper.accessor(row => row.createdAt, {
        id: 'createdAt',
        size: remInPx(12),
        header: () => <span>Created</span>,
        cell: info => {
            return <Tip rest={50} text={info.getValue()?.toLocaleString()}>
                <Copy size="0.9375" flush>{formatDistanceToNow(info.getValue())} ago</Copy>
            </Tip>;
        }
    }),
    columnHelper.accessor(row => row.id, {
        id: 'id',
        size: remInPx(12),
        header: () => <span>Board ID</span>,
        cell: info => <TextCell value={info.getValue()} />
    }),
    columnHelper.accessor(row => row, {
        id: 'archived',
        size: remInPx(16),
        header: () => <span></span>,
        enableSorting: false,
        cell: info => {
            const value = info.getValue();
            if (value.archived) return <BadgeCell color={Colors.observe} value="Archived" />;
            if (!value.workspace) return <BadgeCell color={Colors.gray200} value="Not in a team" />;
            return null;
        }
    }),
    columnHelper.display({
        id: 'null',
        header: () => null,
        cell: () => null,
        enableSorting: false,
        enableHiding: false,
    }),
];

const useSelection = <T extends { id: string }>(table: Table<T>) => {
    const { rows } = table.getRowModel();
    return rows.filter(x => x.getIsSelected()).map(x => x.original);
};

export type BoardTableViewProps = {
    items: BoardExtendedData[],
    onSortChange: (sort: SortMode) => void,
    onSelectionChange: (selection: BoardExtendedData[]) => void,
};

export const BoardTableView = React.memo(({ items, onSortChange, onSelectionChange }: BoardTableViewProps) => {

    useEffect(() => {
        table.toggleAllRowsSelected(false);
    }, [items]);

    const table = useReactTable({
        columns,
        data: items,
        getCoreRowModel: getCoreRowModel(),
        manualSorting: true,
        enableRowSelection: true,
        onStateChange(updater) {
            if (updater instanceof Function) {
                const { sorting } = updater(table.getState());
                if (sorting.length) {
                    onSortChange([sorting[0].id, sorting[0].desc ? 'desc' : 'asc']);
                }
            }
        },
    });

    const selection = useSelection(table);
    useEffect(() => {
        onSelectionChange(selection);
    }, [selection]);

    return <DataTable table={table} selectFromFirstCellOnly theme={TaskTableTheme} />;
});
